// src/features/files/hooks/useAuthenticatedImage.js
import { useState, useEffect } from 'react';
import { apiClient } from '../../../services/api/client';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Hook to fetch and display images with authentication
 * @param {string} fileId - The file ID to fetch
 * @param {string} size - Optional size parameter (e.g., '256x256', 'original')
 * @returns {Object} { imageUrl, loading, error }
 */
export function useAuthenticatedImage(file, size = 'original', rotation = null, version = null) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file?.file_id) {
      setImageUrl(null);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchImage = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (size !== 'original') {
          params.append('size', size);
        }
        if (rotation !== null) {
          params.append('rotation', rotation);
        } else if (file?.rotation) {
          params.append('rotation', file.rotation);
        }
        if (version != null) {
          params.append('version', version);
        }

        const queryString = params.toString();
        const url = `/api/v1/file/files/download/${file.file_id}${queryString ? '?' + queryString : ''}`;

        const response = await apiClient.get(url, {
          responseType: 'blob',
          signal: controller.signal,
          timeout: 60000, // 60 seconds
        });

        if (isMounted) {
          // Create object URL from blob
          const objectUrl = URL.createObjectURL(response.data);
          setImageUrl(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted && err.name !== 'CanceledError') {
          setError(err.message || 'Failed to load image');
          setLoading(false);
        }
      }
    };

    fetchImage();

    // Cleanup function
    return () => {
      isMounted = false;
      controller.abort();
      // Revoke object URL to prevent memory leaks
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [file, size, rotation, version]);

  return { imageUrl, loading, error };
}